import { Mui } from "@osu/react-ui";
import SelectSession from "./Select/Session";
import { useRef, useState } from "react";
import { selectDefaultSession, selectSessionsDropdownOptions } from "../../../services/submissionPeriod/slices";
import { useSelector } from "react-redux";
import { createSelector } from "@reduxjs/toolkit";
import { useGenerateExportMutation } from "../../../services/form/api";
import SelectStatuses from "./Select/Statuses";
import { mergeSxStyles } from "../../../util/functions";
import SelectFormType from "./Select/FormType";

export const selectSessionsLookup = createSelector(
  [selectSessionsDropdownOptions],
  (sessions) =>
    sessions.reduce((acc, session) => {
      acc[session.value] = session;
      return acc;
    }, {})
);

const formFieldNames = {
  session: "session-to-download",
  statuses: "status-filters",
  type: "form-type",
};

const formStyles = {
  [`& > .${Mui.formControlClasses.root}`]: {
    marginRight: 2,
  },
  '& > button[type="submit"]': {
    display: "block",
    marginTop: 3,
  },
  marginTop: 2,
  marginBottom: 2,
};

const createAlertProps = ({ isError, isLoading, isSuccess, fileName }) => {
  const alertProps = {}
  if(isError) {
    alertProps.severity = "error"
    alertProps.children = "An error occurred while generating the export. Please try again."
  } else if (isLoading) {
    alertProps.severity = "info"
    alertProps.children = "Generating export..."
  } else if (isSuccess) {
    alertProps.severity = "success"
    alertProps.children = `Export successfully downloaded` + (fileName ? ` under ${fileName}.` : '.')
  }

  if(alertProps.children) {
    alertProps.component = Mui.Alert
  }
  return alertProps
}

function Form({ sx, ...rest }) {
  const formRef = useRef();
  const downloadLinkRef = useRef()
  const sessionsLookup = useSelector(selectSessionsLookup);
  const [generateExport, { isLoading, isError, isSuccess, data }] = useGenerateExportMutation();
   const defaultSessionValue = useSelector(selectDefaultSession)?.value
  const [session, setSession] = useState(defaultSessionValue || "");

  const download = async (e) => {
    e.preventDefault();
    const formData = new FormData(e?.currentTarget || formRef.current)
    const payload = Object.fromEntries(formData.entries());
    const selectedOptionValue = payload?.[formFieldNames.session];
    const selectedSession =
      selectedOptionValue && sessionsLookup?.[selectedOptionValue];
    const formType = payload?.[formFieldNames.type]
    if (selectedSession?.year && selectedSession?.year && formType) {
      try {
        const result = await generateExport({
          year: selectedSession.year,
          period: selectedSession.period,
          type: formType,
          statuses: payload?.[formFieldNames.statuses],
        }).unwrap();
        const { downloadUrl, fileName } = result || {};
        if (downloadUrl && downloadLinkRef.current) {
          downloadLinkRef.current.href = downloadUrl;
          downloadLinkRef.current.download = fileName;
          downloadLinkRef.current.click();
          
          // Cleanup after download
          setTimeout(() => {
            window.URL.revokeObjectURL(downloadUrl);
          }, 1000);
        }
      } catch (error) {
        console.error("Export failed:", error);
      }
    }
  };

  const alertProps = createAlertProps({
    isLoading, isError, isSuccess, fileName: data?.fileName
  })

  return (
    <Mui.Box
      sx={mergeSxStyles(sx, formStyles)}
      component="form"
      ref={formRef}
      onSubmit={download}
      {...rest}
    >
      <Mui.Box sx={{ mb: 3 }} role="status" aria-live="polite" aria-atomic="true" {...alertProps} />
      <SelectSession 
        name={formFieldNames.session} 
        session={session}
        setSession={setSession}
        defaultValue={defaultSessionValue}
      />
      <SelectFormType name={formFieldNames.type} session={session} />
      <SelectStatuses name={formFieldNames.statuses} />
      <Mui.Button variant="outlined" type="submit" disabled={isLoading}>
        {isLoading ? "Generating..." : "Generate and Download CSV"}
      </Mui.Button>
       <Mui.Box ref={downloadLinkRef} component="a" />
    </Mui.Box>
  );
}

export default Form;
